import { useState } from 'react';
import {
  Stack,
  Button,
  ScrollArea,
  Alert,
  Flex,
  Box,
  Image,
  Group,
  Text,
  Card,
} from '@mantine/core';
import { ResponsiveDrawer } from '../UI/ResponsiveDrawer';
import {
  IconAlertCircle,
  IconCheck,
  IconLink,
  IconFileTypePdf,
} from '@tabler/icons-react';
import { DrawerHeader } from '../UI/DrawerHeader';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../../hooks/useAuth';
import { Recipe } from '../../types/mealPlan';

interface FailedImportsDrawerProps {
  opened: boolean;
  onClose: () => void;
  failedRecipes: Recipe[];
  onRecipesUpdated: () => void;
}

export const FailedImportsDrawer = ({
  opened,
  onClose,
  failedRecipes,
  onRecipesUpdated,
}: FailedImportsDrawerProps) => {
  const { user } = useAuth();
  const [processingItems, setProcessingItems] = useState<Set<string>>(new Set());

  const clearAllFailedImports = async () => {
    if (!user || processingItems.size > 0) return;

    // Add all recipe IDs to processing state
    const allRecipeIds = new Set(failedRecipes.map(recipe => recipe.id));
    setProcessingItems(allRecipeIds);

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/failed`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to clear all imports');
      }

      notifications.show({
        title: 'Imports cleared',
        message: 'All failed imports have been removed',
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 6000,
      });

      onRecipesUpdated();
      onClose();
    } catch (error: any) {
      console.error('Error clearing all failed imports:', error);
      notifications.show({
        title: 'Clear failed',
        message: error.message || 'Failed to clear imports. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 10000,
      });
    } finally {
      setProcessingItems(new Set());
    }
  };

  return (
    <ResponsiveDrawer
      opened={opened}
      onClose={onClose}
      title={
        <DrawerHeader
          icon={<IconAlertCircle size={18} />}
          title="Recipe import failed"
        />
      }
      size="lg"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="lg">
        {failedRecipes.length === 0 ? (
          <Alert icon={<IconAlertCircle size={16} color="green" />} color="green" radius="lg">
            <Text size="sm">No failed imports to show. All recipe imports completed successfully!</Text>
          </Alert>
        ) : (
          <>
            {/* Helpful message for users */}
            <Text size="sm" c="dimmed">
              These recipe imports couldn't be processed. For images, use clear photos or screenshots with visible ingredients and instructions. For URLs, ensure the webpage contains a complete recipe.
            </Text>
            {/* Show thumbnails grouped by import in cards */}
            <Stack gap="md">
              {failedRecipes.map((recipe, index) => (
                <Card key={recipe.id} p="md" withBorder radius="lg" style={{ borderColor: 'var(--mantine-color-red-3)' }}>
                  <Stack gap="sm">
                    {/* Timestamp */}
                    {recipe.createdAt && (
                      <Text size="xs" c="dimmed">
                        {new Date(recipe.createdAt).toLocaleString()}
                      </Text>
                    )}
                    
                    {/* Display content based on import type */}
                    {recipe.importStatus?.sourceUrl ? (
                      /* URL Import Display */
                      <Group gap="sm">
                        <Box
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 'var(--mantine-radius-sm)',
                            backgroundColor: 'var(--mantine-color-orange-1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid var(--mantine-color-orange-3)',
                            flexShrink: 0,
                          }}
                        >
                          <IconLink size={20} color="var(--mantine-color-orange-6)" />
                        </Box>
                        <Stack gap={2} style={{ flex: 1 }}>
                          <Text size="sm" fw={500}>URL import failed</Text>
                          <Text 
                            size="xs" 
                            c="dimmed" 
                            style={{ 
                              wordBreak: 'break-all',
                              lineHeight: 1.2
                            }}
                          >
                            {recipe.importStatus.sourceUrl}
                          </Text>
                          {recipe.importStatus.errorMessage && (
                            <Text 
                              size="xs" 
                              c="red.6" 
                              style={{ 
                                lineHeight: 1.3,
                                marginTop: 4
                              }}
                            >
                              <strong>Error:</strong> {recipe.importStatus.errorMessage}
                            </Text>
                          )}
                        </Stack>
                      </Group>
                    ) : recipe.importStatus?.imageUrls && recipe.importStatus.imageUrls.length > 0 ? (
                      /* Image Import Display */
                      <Stack gap="sm">
                        <Text size="sm" fw={500}>
                          {recipe.importStatus.imageUrls.some((url) => url.toLowerCase().endsWith('.pdf'))
                            ? 'File import failed'
                            : 'Image import failed'}
                        </Text>
                        <Flex gap="xs" wrap="wrap">
                          {recipe.importStatus.imageUrls.map((imageUrl, imgIndex) => (
                            <Box
                              key={imgIndex}
                              style={{
                                width: 80,
                                height: 80,
                                borderRadius: 'var(--mantine-radius-sm)',
                                overflow: 'hidden',
                                border: '1px solid var(--mantine-color-gray-3)',
                                flexShrink: 0,
                              }}
                            >
                              {imageUrl.toLowerCase().endsWith('.pdf') ? (
                                <Flex w={80} h={80} align="center" justify="center" bg="var(--mantine-color-gray-0)">
                                  <IconFileTypePdf size={32} stroke={1.5} color="var(--mantine-color-red-6)" />
                                </Flex>
                              ) : (
                                <Image
                                  src={imageUrl}
                                  alt={`Failed upload ${imgIndex + 1}`}
                                  width={80}
                                  height={80}
                                  fit="cover"
                                  fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Crect width='80' height='80' fill='%23f1f3f4'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23666' font-size='12'%3EImage%3C/text%3E%3C/svg%3E"
                                  />
                              )}
                              </Box>
                            ))}
                          </Flex>
                        {recipe.importStatus.errorMessage && (
                          <Text 
                            size="xs" 
                            c="red.6" 
                            style={{ 
                              lineHeight: 1.3,
                              marginTop: 4
                            }}
                          >
                            <strong>Error:</strong> {recipe.importStatus.errorMessage}
                          </Text>
                        )}
                        </Stack>
                      ) : (
                        /* Generic fallback */
                        <Stack gap="sm">
                          <Text size="sm" fw={500}>Recipe import failed</Text>
                          <Box
                            style={{
                              width: 80,
                              height: 80,
                              borderRadius: 'var(--mantine-radius-sm)',
                              backgroundColor: 'var(--mantine-color-gray-1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid var(--mantine-color-gray-3)',
                              flexShrink: 0,
                            }}
                          >
                            <IconAlertCircle size={32} color="var(--mantine-color-gray-5)" />
                          </Box>
                          {recipe.importStatus?.errorMessage && (
                            <Text 
                              size="xs" 
                              c="red.6" 
                              style={{ 
                                lineHeight: 1.3,
                                marginTop: 4
                              }}
                            >
                              <strong>Error:</strong> {recipe.importStatus.errorMessage}
                            </Text>
                          )}
                        </Stack>
                      )}
                  </Stack>
                </Card>
              ))}
            </Stack>

            {/* Action Buttons */}
            <Group justify="flex-end" mt="lg">
              <Button
                color="orange"
                onClick={clearAllFailedImports}
                loading={processingItems.size > 0}
              >
                Clear all
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </ResponsiveDrawer>
  );
};
